import React from "react";
import { Progress } from "antd";
import type { NotificationInstance } from "antd/es/notification/interface";

export type DownloadFileProgress = {
  index: number;
  fileName: string;
  loaded: number;
  total: number | null;
};

export function createDownloadProgressNotificationKey(): string {
  return `download-progress-${Date.now()}`;
}

/** 原有的单文件进度更新（兼容其他调用方） */
export function updateDownloadProgressNotification(
  api: NotificationInstance,
  key: string,
  progress: number,
  text: string
): void {
  api.open({
    key,
    message: "下载进度",
    description: (
      <div>
        <div style={{ marginBottom: "8px", fontSize: "14px" }}>{text}</div>
        <Progress
          percent={progress}
          size="small"
          status={progress < 100 ? "active" : "normal"}
          strokeColor="#1a1a1a"
          showInfo={false}
        />
      </div>
    ),
    duration: 0,
    placement: "topRight",
    style: {
      width: 320,
      minHeight: 80,
    },
    className: "download-progress-notification",
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** 多文件并发下载进度更新：每个活跃文件显示独立进度条 + 取消按钮 */
export function updateStreamingDownloadProgress(
  api: NotificationInstance,
  key: string,
  completed: number,
  total: number,
  files: DownloadFileProgress[],
  actions?: { cancelFile: (index: number) => void; cancelAll: () => void }
): void {
  const allDone = completed >= total && files.length === 0;

  api.open({
    key,
    message: `导出进度（${completed}/${total}）`,
    onClose: actions?.cancelAll,
    description: (
      <div style={{ maxHeight: 300, overflowY: "auto" }}>
        {files.length > 0 ? (
          files.map((f, i) => {
            const pct =
              f.total && f.total > 0
                ? Math.round((f.loaded / f.total) * 100)
                : 0;
            const sizeText =
              f.total && f.total > 0
                ? `${formatSize(f.loaded)}/${formatSize(f.total)}`
                : `${formatSize(f.loaded)}`;
            return (
              <div key={`${f.fileName}-${i}`} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 2,
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 180,
                      flex: 1,
                    }}
                  >
                    {f.fileName}
                  </span>
                  <span style={{ marginLeft: 8, whiteSpace: "nowrap" }}>{sizeText}</span>
                  {actions?.cancelFile && (
                    <span
                      role="button"
                      onClick={() => actions.cancelFile(f.index)}
                      style={{
                        marginLeft: 6,
                        cursor: "pointer",
                        color: "#999",
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                      title="取消此文件"
                    >
                      ✕
                    </span>
                  )}
                </div>
                <Progress
                  percent={pct}
                  size="small"
                  status={pct < 100 ? "active" : "normal"}
                  strokeColor="#1a1a1a"
                  showInfo={false}
                />
              </div>
            );
          })
        ) : allDone ? null : (
          <div style={{ fontSize: 12, color: "#999" }}>等待中…</div>
        )}
      </div>
    ),
    duration: allDone ? 2 : 0,
    placement: "topRight",
    style: {
      width: 360,
      minHeight: 80,
    },
    className: "download-progress-notification",
  });
}
